import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { cloneDeep } from 'lodash-es'

import {
  IErrorData,
  IErrorPayload,
  IFilterProfile,
  INetwork,
  IOperator,
  IRegion,
  IState
} from '$/types'

import {
  ISystemScopeFilterProfileValidationErrors,
  ISystemScopeFilterProfileAPIRequestStatus,
  IGlobalFilterModal
} from './types'

const initialState: IGlobalFilterModal = {
  systemScopeIdContainer: {
    id: undefined,
    updatedAt: 0
  },
  stagingProfileValidationErrors: {},
  stagingProfileAPIRequestStatus: {},
  filteredOperators: [],
  filteredStates: [],
  filteredRegions: [],
  filteredNetworks: [],
  attributes: [],
  editInProgress: false
}

export const globalFilterSlice = createSlice({
  name: 'globalFilters',
  initialState,
  reducers: {
    setSystemScopeId: (state, action: PayloadAction<string | undefined>) => {
      state.systemScopeIdContainer = {
        id: action.payload ?? undefined,
        updatedAt: +new Date()
      }
    },
    setStagingProfileContinousRoutes: (
      state,
      action: PayloadAction<IFilterProfile['continuousRoutes']>
    ) => {
      if (state.stagingProfile) {
        state.stagingProfile.continuousRoutes = action.payload
      }
    },
    setStagingProfile: (
      state,
      action: PayloadAction<IFilterProfile | undefined>
    ) => {
      state.stagingProfile = action.payload ?? undefined
    },
    setStagingProfilePending: (state) => {
      return {
        ...state,
        stagingProfileAPIRequestStatus: {
          ...state.stagingProfileAPIRequestStatus,
          isSuccess: undefined
        }
      }
    },
    setStagingProfileFulfilled: (
      state,
      action: PayloadAction<IFilterProfile | undefined>
    ) => {
      return {
        ...state,
        stagingProfile: action.payload ?? undefined,
        stagingProfileAPIRequestStatus: {
          ...state.stagingProfileAPIRequestStatus,
          isSuccess: true,
          latestSuccessTimestamp: Date.now()
        }
      }
    },
    setStagingProfileRejected: (
      state,
      action: PayloadAction<IErrorPayload | undefined>
    ) => {
      const errors: IErrorData[] = action.payload?.response?.errors ?? []

      const validationErrors = cloneDeep(state.stagingProfileValidationErrors)

      if (errors.some((e) => e.code === 704)) {
        validationErrors.nameTaken = true
      }

      return {
        ...state,
        stagingProfileValidationErrors: validationErrors,
        stagingProfileAPIRequestStatus: {
          ...state.stagingProfileAPIRequestStatus,
          isSuccess: false
        }
      }
    },
    setStagingProfileValidationErrors: (
      state,
      action: PayloadAction<ISystemScopeFilterProfileValidationErrors>
    ) => {
      state.stagingProfileValidationErrors = action.payload
    },
    setStagingProfileAPIRequestStatus: (
      state,
      action: PayloadAction<ISystemScopeFilterProfileAPIRequestStatus>
    ) => {
      state.stagingProfileAPIRequestStatus = action.payload
    },

    setFilteredOperatorList: (
      state,
      action: PayloadAction<IOperator[] | undefined>
    ) => {
      state.filteredOperators = action.payload ?? []
    },
    setFilteredStateList: (
      state,
      action: PayloadAction<IState[] | undefined>
    ) => {
      state.filteredStates = action.payload ?? []
    },
    setFilteredRegionList: (
      state,
      action: PayloadAction<IRegion[] | undefined>
    ) => {
      state.filteredRegions = action.payload ?? []
    },
    setFilteredNetworkList: (
      state,
      action: PayloadAction<INetwork[] | undefined>
    ) => {
      state.filteredNetworks = action.payload ?? []
    },

    setEditInProgress: (state, action: PayloadAction<boolean>) => {
      state.editInProgress = action.payload
    }
  }
})

export const {
  setSystemScopeId: setSystemScopeIdSync,

  setStagingProfile: setStagingProfileSync,
  setStagingProfilePending: setStagingProfilePendingSync,
  setStagingProfileFulfilled: setStagingProfileFulfilledSync,
  setStagingProfileRejected: setStagingProfileRejectedSync,
  setStagingProfileContinousRoutes: setStagingProfileContinousRoutesSync,
  setStagingProfileValidationErrors: setStagingProfileValidationErrorsSync,
  setStagingProfileAPIRequestStatus: setStagingProfileAPIRequestStatusSync,

  setFilteredOperatorList: setFilteredOperatorListSync,
  setFilteredStateList: setFilteredStateListSync,
  setFilteredRegionList: setFilteredRegionListSync,
  setFilteredNetworkList: setFilteredNetworkListSync,

  setEditInProgress: setEditInProgressSync
} = globalFilterSlice.actions

export default globalFilterSlice.reducer
